/**
 * 租户管理 API
 */
import { request } from './api';

export interface Tenant {
  id: string;
  name: string;
  display_name: string;
  logo_url?: string;
  custom_domain?: string;
  status: 'active' | 'inactive' | 'suspended';
  plan_type: string;
  total_seats: number;
  used_seats: number;
  created_at: string;
  updated_at: string;
}

export interface TenantUser {
  id: string;
  tenant_id: string;
  user_id: string;
  role: string;
  status: string;
  joined_at?: string;
  user?: {
    id: string;
    name: string;
    email: string;
    avatar_url?: string;
  };
}

export interface CreateTenantRequest {
  id: string;          // 租户ID（URL路径使用，如 nuphy）
  name: string;        // 租户名称
  display_name?: string; // 显示名称
  plan_type?: string;  // 套餐类型
  total_seats?: number; // 总席位数
  // 租户管理员信息
  admin_email: string;    // 管理员邮箱
  admin_password: string; // 管理员密码
  admin_name?: string;    // 管理员名称
}

export interface TenantCheckResult {
  exists: boolean;
  id: string;
  display_name?: string;
  logo_url?: string;
}

// 检查租户是否存在（公开接口，无需登录）
export async function checkTenantExists(tenantId: string): Promise<TenantCheckResult> {
  const response = await fetch(`/api/tenants/${tenantId}/check`);
  if (!response.ok) {
    return { exists: false, id: tenantId };
  }
  const json = await response.json();
  return json.data as TenantCheckResult;
}

// 获取租户列表（管理员）
export function listTenants(status?: string): Promise<Tenant[]> {
  const query = status ? `?status=${status}` : '';
  return request<Tenant[]>(`/tenants${query}`);
}

// 获取租户详情
export function getTenant(id: string): Promise<Tenant> {
  return request<Tenant>(`/tenants/${id}`);
}

// 创建租户（管理员）
export function createTenant(data: CreateTenantRequest): Promise<Tenant> {
  return request<Tenant>('/tenants', {
    method: 'POST',
    body: data,
  });
}

// 更新租户
export function updateTenant(id: string, updates: Partial<Tenant> & { reset_password?: string }): Promise<void> {
  return request(`/tenants/${id}`, {
    method: 'PUT',
    body: updates,
  });
}

// 删除/禁用租户
export function deleteTenant(id: string): Promise<void> {
  return request(`/tenants/${id}`, {
    method: 'DELETE',
  });
}

// 获取租户用户列表
export function getTenantUsers(tenantId: string): Promise<TenantUser[]> {
  return request<TenantUser[]>(`/tenants/${tenantId}/users`);
}

// 添加用户到租户
export function addUserToTenant(tenantId: string, userId: string, role?: string): Promise<TenantUser> {
  return request<TenantUser>(`/tenants/${tenantId}/users`, {
    method: 'POST',
    body: { user_id: userId, role: role || 'member' },
  });
}

// 从租户移除用户
export function removeUserFromTenant(tenantId: string, userId: string): Promise<void> {
  return request(`/tenants/${tenantId}/users/${userId}`, {
    method: 'DELETE',
  });
}

// 获取当前用户所属的租户列表
export function getMyTenants(): Promise<TenantUser[]> {
  return request<TenantUser[]>('/my-tenants');
}

// 获取当前租户信息
export function getCurrentTenant(): Promise<Tenant> {
  return request<Tenant>('/tenant');
}
